import { ConnectionPositionPair } from '@angular/cdk/overlay';
import { HttpClient } from '@angular/common/http';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  inject,
  OnDestroy,
  TemplateRef,
  ViewChild,
} from '@angular/core';
import { FormControl } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { Email, EmailActivity, EventType } from '@vsolv/common/email/domain';
import { PaginationQueryResponse } from '@vsolv/dev-kit/ngx';
import { ThemeColor } from '@vsolv/vectors-ui/theming';
import { BadgeColumn, PaginationConfig, TextColumn } from '@vsolv/vectors/table';
import moment from 'moment';
import {
  BehaviorSubject,
  catchError,
  combineLatest,
  debounceTime,
  distinctUntilChanged,
  map,
  of,
  shareReplay,
  startWith,
  Subject,
  switchMap,
  takeUntil,
  tap,
} from 'rxjs';

type Item = Pick<Email, 'id' | 'created' | 'to' | 'subject'> & {
  activity: Pick<EmailActivity, 'timestamp' | 'eventType'>[];
};

type DateRangeInput = Date | { startDate: Date | null; endDate: Date | null } | null;

type EmailFilters = {
  search: string | null;
  statuses: EventType[];
  startDate: Date | null;
  endDate: Date | null;
};

const STATUS_OPTIONS: { value: EventType; label: string }[] = [
  { value: EventType.SEND, label: 'Sent' },
  { value: EventType.DELIVERY, label: 'Delivered' },
  { value: EventType.OPEN, label: 'Opened' },
  { value: EventType.CLICK, label: 'Clicked' },
  { value: EventType.BOUNCE, label: 'Bounced' },
];

@Component({
  templateUrl: './emails.page.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
  styles: [
    `
      #indeterminate-progress-bar-value {
        transform-origin: 0% 50%;
        animation: indeterminate 1.5s linear infinite;
      }

      @keyframes indeterminate {
        0% {
          transform: translateX(0) scaleX(0);
        }
        40% {
          transform: translateX(0) scaleX(0.5);
        }
        100% {
          transform: translateX(100%) scaleX(0.5);
        }
      }
    `,
  ],
})
export class EmailsPage implements OnDestroy {
  private router = inject(Router);
  private http = inject(HttpClient);
  private route = inject(ActivatedRoute);
  private changeDetector = inject(ChangeDetectorRef);

  @ViewChild('sentOnTooltip') private sentOnTooltip?: TemplateRef<unknown>;
  @ViewChild('baseTooltip') private baseTooltip?: TemplateRef<unknown>;
  @ViewChild('statusTooltip') private statusTooltip?: TemplateRef<ElementRef>;

  private readonly initialFilters = this.readFiltersFromQueryParams();

  readonly searchFormControl = new FormControl<string | null>(this.initialFilters.search);
  readonly statusesControl = new FormControl<EventType[]>(this.initialFilters.statuses, { nonNullable: true });
  readonly startDateControl = new FormControl<Date | null>(this.initialFilters.startDate);
  readonly endDateControl = new FormControl<Date | null>(this.initialFilters.endDate);

  readonly statusOptions = STATUS_OPTIONS;

  readonly positions = [
    new ConnectionPositionPair({ originX: 'end', originY: 'bottom' }, { overlayX: 'end', overlayY: 'top' }),
    new ConnectionPositionPair({ originX: 'end', originY: 'top' }, { overlayX: 'end', overlayY: 'bottom' }),
    new ConnectionPositionPair({ originX: 'start', originY: 'bottom' }, { overlayX: 'start', overlayY: 'top' }),
    new ConnectionPositionPair({ originX: 'start', originY: 'top' }, { overlayX: 'start', overlayY: 'bottom' }),
  ];

  isFiltersOverlayOpen = false;

  readonly loading$ = new BehaviorSubject(true);
  readonly pagination$ = new BehaviorSubject({ limit: 10, page: 1 });
  private sort$ = new BehaviorSubject<'ASC' | 'DESC'>('DESC');
  private readonly destroy$ = new Subject<void>();

  // The repeated loading = true pipe is necessary because of the debounce (doesn't trigger first until 300ms after)
  private filters$ = combineLatest([
    this.searchFormControl.valueChanges.pipe(
      startWith(this.searchFormControl.value),
      tap(() => this.loading$.next(true)),
      debounceTime(300),
      distinctUntilChanged()
    ),
    this.statusesControl.valueChanges.pipe(
      startWith(this.statusesControl.value),
      tap(() => this.loading$.next(true))
    ),
    this.startDateControl.valueChanges.pipe(
      startWith(this.startDateControl.value),
      tap(() => this.loading$.next(true))
    ),
    this.endDateControl.valueChanges.pipe(
      startWith(this.endDateControl.value),
      tap(() => this.loading$.next(true))
    ),
  ]).pipe(
    map(
      ([search, statuses, startDate, endDate]) =>
        <EmailFilters>{ search: search?.trim() || null, statuses, startDate, endDate }
    ),
    tap(filters => {
      this.syncFiltersToQueryParams(filters);
      if (this.pagination$.value.page !== 1) {
        this.pagination$.next({ ...this.pagination$.value, page: 1 });
      }
    }),
    takeUntil(this.destroy$)
  );

  private pageData$ = combineLatest([
    this.pagination$.pipe(tap(() => this.loading$.next(true))),
    this.sort$.pipe(tap(() => this.loading$.next(true))),
    this.filters$,
  ]).pipe(
    switchMap(([{ limit, page }, sort, filters]) => {
      return this.http.get<PaginationQueryResponse<Item>>('/api/emails', {
        params: {
          limit: limit.toString(),
          page: page.toString(),
          search: filters.search ?? '',
          sort,
          ...(filters.startDate ? { startDate: this.formatDateParam(filters.startDate) } : {}),
          ...(filters.endDate ? { endDate: this.formatDateParam(filters.endDate) } : {}),
          ...(filters.statuses.length ? { statuses: filters.statuses } : {}),
        },
      });
    }),
    catchError(() => of(null)),
    tap(() => this.loading$.next(false)),
    shareReplay(1)
  );

  readonly meta$ = this.pageData$.pipe(map(data => data?.meta));
  readonly items$ = this.pageData$.pipe(
    map(data => data?.items ?? []),
    tap(() => setTimeout(() => this.changeDetector.markForCheck()))
  );

  readonly paginationConfig$ = this.meta$.pipe(
    startWith(null),
    map(meta => {
      return <PaginationConfig>{
        itemsPerPage: meta?.itemsPerPage ?? 10,
        currentPage: meta?.currentPage ?? 1,
        totalItems: meta?.totalItems ?? 0,
        pageSizes: [10, 20, 50],
      };
    })
  );

  readonly tableColumns = [
    new TextColumn(
      {
        header: 'Sent on',
        fitContent: true,
        sortable: true,
        allowUnsorted: false,
        sort: this.sort$.value,
        sortChanged: sort => {
          this.sort$.next(sort ?? 'DESC');
        },
      },
      (item: Item) => {
        return {
          classes: 'truncate',
          text: moment(item.created).format('L hh:mm:ss A'),
          tooltip: this.sentOnTooltip && {
            template: this.sentOnTooltip,
            context: {
              local: moment(item.created).format('ll LTS'),
              utc: moment(item.created).utc().toISOString(),
            },
          },
        };
      }
    ),

    new TextColumn({ header: 'Recipient', fitContent: true }, (item: Item) => {
      return {
        classes: 'truncate max-w-[200px]',
        text: item.to,
        tooltip: this.baseTooltip && {
          template: this.baseTooltip,
          context: {
            title: 'Sent to',
            subtitle: item.to,
          },
        },
      };
    }),

    new BadgeColumn({ header: 'Status', fitContent: true }, (item: Item) => {
      const status = emailStatus(item.activity);

      return {
        text: status?.text,
        theme: status?.theme,
        ...(status &&
          this.statusTooltip && {
            tooltipStyle: 'light',
            tooltipTemplate: this.statusTooltip,
            tooltipContext: {
              text: status.text,
              timestamp: moment(status.timestamp).format('ll LTS'),
              description: status.description,
            },
          }),
      };
    }),

    new TextColumn({ header: 'Subject' }, (item: Item) => {
      return {
        classes: 'truncate max-w-sm',
        text: item.subject,
        tooltip: this.baseTooltip && {
          template: this.baseTooltip,
          context: {
            title: 'Subject',
            subtitle: item.subject,
          },
        },
      };
    }),
  ];

  get activeFilterCount(): number {
    let count = 0;
    if (this.statusesControl.value.length) count++;
    if (this.startDateControl.value || this.endDateControl.value) count++;
    return count;
  }

  toggleFiltersOverlay() {
    this.isFiltersOverlayOpen = !this.isFiltersOverlayOpen;
  }

  closeFiltersOverlay() {
    this.isFiltersOverlayOpen = false;
  }

  clearAllFilters() {
    this.statusesControl.setValue([]);
    this.startDateControl.setValue(null);
    this.endDateControl.setValue(null);
  }

  onDateRangeChange(dateRange: DateRangeInput) {
    if (dateRange instanceof Date) {
      // Single date selection filters that one day
      this.startDateControl.setValue(dateRange);
      this.endDateControl.setValue(dateRange);
    } else if (dateRange && typeof dateRange === 'object' && 'startDate' in dateRange) {
      this.startDateControl.setValue(dateRange.startDate ?? null);
      this.endDateControl.setValue(dateRange.endDate ?? null);
    } else if (dateRange === null) {
      this.startDateControl.setValue(null);
      this.endDateControl.setValue(null);
    }
  }

  async navigateTo(emailId: string) {
    await this.router.navigate([emailId], { relativeTo: this.route }).catch();
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private readFiltersFromQueryParams(): EmailFilters {
    const params = this.route.snapshot.queryParamMap;

    const statuses = (params.get('statuses')?.split(',') ?? []).filter((value): value is EventType =>
      STATUS_OPTIONS.some(option => option.value === value)
    );

    return {
      search: params.get('search'),
      statuses,
      startDate: this.parseDateParam(params.get('startDate')),
      endDate: this.parseDateParam(params.get('endDate')),
    };
  }

  private syncFiltersToQueryParams(filters: EmailFilters) {
    void this.router.navigate([], {
      relativeTo: this.route,
      queryParams: {
        search: filters.search || null,
        statuses: filters.statuses.length ? filters.statuses.join(',') : null,
        startDate: filters.startDate ? this.formatDateParam(filters.startDate) : null,
        endDate: filters.endDate ? this.formatDateParam(filters.endDate) : null,
      },
      queryParamsHandling: 'merge',
      replaceUrl: true,
    });
  }

  private parseDateParam(value: string | null): Date | null {
    if (!value) return null;

    const parsed = moment(value, 'YYYY-MM-DD', true);
    return parsed.isValid() ? parsed.toDate() : null;
  }

  private formatDateParam(value: Date): string {
    return moment(value).format('YYYY-MM-DD');
  }
}

export function emailStatus(
  activities: Item['activity']
): { text: string; theme: ThemeColor; description: string; timestamp: Date } | undefined {
  const clicked = activities.find(activity => activity.eventType === EventType.CLICK);
  if (clicked) {
    return {
      timestamp: clicked.timestamp,
      text: 'Clicked',
      theme: 'success',
      description: 'The recipient clicked a link inside the email body.',
    };
  }

  const bounced = activities.find(activity => activity.eventType === EventType.BOUNCE);
  if (bounced) {
    return {
      timestamp: bounced.timestamp,
      text: 'Bounced',
      theme: 'danger',
      description:
        'The email bounced and was not delivered to the recipient. This is typically due to an invalid/non-existent email address.',
    };
  }

  const opened = activities.find(activity => activity.eventType === EventType.OPEN);
  if (opened) {
    return {
      timestamp: opened.timestamp,
      text: 'Opened',
      theme: 'info',
      description:
        'The recipient opened the email. This can sometimes be triggered by email clients (e.g. Outlook, Gmail, etc.) and does not guarantee the recipient actually opened/read the email.',
    };
  }

  const delivered = activities.find(activity => activity.eventType === EventType.DELIVERY);
  if (delivered) {
    return {
      timestamp: delivered.timestamp,
      text: 'Delivered',
      theme: 'default',
      description:
        "The email was delivered via the internet and is waiting for the recipient's mail server to receive/process it.",
    };
  }

  const sent = activities.find(activity => activity.eventType === EventType.SEND);
  if (sent) {
    return {
      timestamp: sent.timestamp,
      text: 'Sent',
      theme: 'default',
      description: "The email was sent to WarrantySphere's mail server and is pending delivery.",
    };
  }

  return undefined;
}
